import React from 'react';
import { ComponentData } from '../../types';
import { Card } from '../ui/Card';
import { classNames } from '../../utils/classNames';

interface TableRendererProps {
  data: ComponentData;
  title?: string;
  maxRows?: number;
  accent?: 'blue' | 'yellow' | 'purple' | 'green' | 'indigo' | 'emerald' | 'orange' | 'cyan';
}

export const TableRenderer: React.FC<TableRendererProps> = ({ 
  data, 
  title, 
  maxRows = 10,
  accent
}) => {
  const tableRows = data.table_rows || [];
  
  if (!tableRows.length) {
    return (
      <Card accent={accent}>
        {title && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold theme-text-analysis-primary">{title}</h3>
          </div>
        )}
        <div className="text-center py-8">
          <p className="theme-text-analysis-muted">No data available</p>
        </div>
      </Card>
    );
  }

  const columns = Object.keys(tableRows[0]);
  const displayRows = tableRows.slice(0, maxRows);
  const hasMoreRows = tableRows.length > maxRows;

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') {
      // Format numbers with appropriate precision
      if (value % 1 === 0) return value.toLocaleString();
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  };

  const getColumnAlignment = (column: string, values: any[]): 'left' | 'center' | 'right' => {
    // Check if all values in this column are numbers
    const isNumeric = values.every(val => 
      val === null || val === undefined || typeof val === 'number'
    );
    
    if (isNumeric) return 'right';
    if (column.toLowerCase().includes('rate') || column.toLowerCase().includes('%')) return 'center';
    return 'left';
  };

  return (
    <Card padding="none" accent={accent}>
      {title && (
        <div className="px-4 py-3 border-b theme-border-table">
          <h3 className="text-lg font-semibold theme-text-analysis-primary">{title}</h3>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y theme-border-table">
          <thead className="theme-bg-table-header">
            <tr>
              {columns.map((column) => {
                const alignment = getColumnAlignment(column, tableRows.map(row => row[column]));
                return (
                  <th
                    key={column}
                    className={classNames(
                      'px-4 py-2 text-xs font-medium theme-text-table-header uppercase tracking-wider',
                      alignment === 'left' && 'text-left',
                      alignment === 'center' && 'text-center',
                      alignment === 'right' && 'text-right'
                    )}
                  >
                    {column.replace(/_/g, ' ')}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="theme-bg-table-body divide-y theme-border-table">
            {displayRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:theme-bg-table-row-hover">
                {columns.map((column) => {
                  const value = row[column];
                  const alignment = getColumnAlignment(column, tableRows.map(r => r[column]));
                  
                  return (
                    <td
                      key={column}
                      className={classNames(
                        'px-4 py-3 whitespace-nowrap text-sm theme-text-table-body',
                        alignment === 'left' && 'text-left',
                        alignment === 'center' && 'text-center',
                        alignment === 'right' && 'text-right'
                      )}
                    >
                      {formatCellValue(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {hasMoreRows && (
        <div className="px-4 py-2 theme-bg-table-footer border-t theme-border-table">
          <p className="text-sm theme-text-table-footer text-center">
            Showing {displayRows.length} of {tableRows.length} rows
          </p>
        </div>
      )}
    </Card>
  );
};